<template>
  <div class="saved-cards-container">
    <div v-if="isPreview" class="message info">預覽模式無法載入實際卡片資料，請於實際安裝的 location 中查看。</div>

    <div v-else-if="loading" class="loading">
      <div class="spinner"></div>
      <p>正在載入卡片...</p>
    </div>

    <div v-else-if="error" class="message error">{{ error }}</div>

    <div v-else-if="cards.length === 0" class="empty-state">
      <p>此 Location 目前沒有已存的約定信用卡。</p>
    </div>

    <table v-else class="cards-table">
      <thead>
        <tr>
          <th>會員</th>
          <th>卡別</th>
          <th>末四碼</th>
          <th>到期日</th>
          <th>建立時間</th>
          <th>動作</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="card in cards" :key="card.id">
          <td>
            <span v-if="card.contactName">{{ card.contactName }}</span>
            <span v-else class="unknown-name" title="此卡未記錄會員姓名；重新綁卡且 GHL 有提供姓名時才會顯示">未記錄姓名</span>
            <div class="contact-id">聯絡人 ID（Contact ID）：{{ card.contactId }}</div>
          </td>
          <td>{{ card.cardType }}</td>
          <td>{{ card.cardLast4 }}</td>
          <td>{{ card.cardExpiry }}</td>
          <td>{{ formatDate(card.createdAt) }}</td>
          <td>
            <button
              type="button"
              class="delete-button"
              :disabled="deletingId === card.id"
              @click="confirmDelete(card)"
            >
              {{ deletingId === card.id ? '刪除中…' : '刪除' }}
            </button>
          </td>
        </tr>
      </tbody>
    </table>

    <div v-if="deleteError" class="message error">{{ deleteError }}</div>
  </div>
</template>

<script>
/**
 * 操作人員「已存卡片」分頁（ADR 0001 方案 A 第一半，R5 票 06）。
 * locationId 由後端解密 GHL Custom Page SSO context 取得，本元件只負責取得
 * 尚未解密的 SSO key（window.ghl.requestSsoKey()）並原封轉送，不自行判斷或
 * 傳送 locationId。
 */
export default {
  name: 'SavedCardsList',
  props: {
    isPreview: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      cards: [],
      loading: true,
      error: '',
      deletingId: null,
      deleteError: '',
    };
  },
  async mounted() {
    if (this.isPreview) {
      this.loading = false;
      return;
    }
    await this.loadCards();
  },
  methods: {
    /**
     * `/api/payment-method/operator/cards`（列卡）與 `/api/payment-method/:id/delete`
     * （刪卡）共用的請求形狀：取一把新的 SSO key、POST、解析 JSON、把「非成功」統一轉成
     * 一個帶訊息的 Error。呼叫端各自決定錯誤要顯示什麼預設訊息。
     */
    async postOperatorRequest(url, defaultErrorMessage) {
      const key = await window.ghl.requestSsoKey();
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ key }),
      });

      const result = await response.json().catch(() => ({}));
      if (!response.ok || !result.success) {
        throw new Error(result.message || defaultErrorMessage);
      }
      return result;
    },

    async loadCards() {
      this.loading = true;
      this.error = '';
      try {
        const result = await this.postOperatorRequest('/api/payment-method/operator/cards', '載入卡片列表失敗');
        this.cards = result.cards || [];
      } catch (err) {
        console.error('Failed to load saved cards:', err);
        this.error = err.message || '載入卡片列表失敗';
      } finally {
        this.loading = false;
      }
    },

    /**
     * 刪除前的二次確認（native confirm，與 NewebPayCheckout.vue 既有的確認對話模式一致）；
     * 確認後才呼叫 `POST /api/payment-method/:id/delete`。key 每次刪除都重新
     * 向 GHL 要一把新的（`postOperatorRequest` 內的 `window.ghl.requestSsoKey()`），
     * 不重用 loadCards() 當初拿到的那把。
     */
    async confirmDelete(card) {
      this.deleteError = '';

      const memberLabel = card.contactName
        ? `${card.contactName}（聯絡人 ID：${card.contactId}）`
        : `聯絡人 ID：${card.contactId}（未記錄姓名，請務必先核對是否為正確會員）`;
      const confirmed = window.confirm(
        `確定要刪除這張卡片嗎？\n會員：${memberLabel}\n卡別：${card.cardType}，末四碼：${card.cardLast4}\n此動作無法復原。`
      );
      if (!confirmed) return;

      this.deletingId = card.id;
      try {
        await this.postOperatorRequest(`/api/payment-method/${card.id}/delete`, '刪除失敗');
        await this.loadCards();
      } catch (err) {
        console.error('Failed to delete payment method:', err);
        this.deleteError = err.message || '刪除失敗';
      } finally {
        this.deletingId = null;
      }
    },

    formatDate(value) {
      if (!value) return '';
      try {
        return new Date(value).toLocaleString('zh-TW');
      } catch {
        return String(value);
      }
    },
  },
};
</script>

<style scoped>
.saved-cards-container {
  min-height: 4rem;
}

.loading {
  text-align: center;
  padding: 2rem;
}

.spinner {
  border: 3px solid #f3f3f3;
  border-top: 3px solid #007bff;
  border-radius: 50%;
  width: 30px;
  height: 30px;
  animation: spin 1s linear infinite;
  margin: 0 auto 1rem;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.empty-state {
  text-align: center;
  padding: 2rem;
  color: #666;
}

.cards-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.9rem;
}

.cards-table th,
.cards-table td {
  text-align: left;
  padding: 0.75rem;
  border-bottom: 1px solid #e9ecef;
}

.unknown-name {
  color: #999;
  font-style: italic;
}

.contact-id {
  margin-top: 0.15rem;
  font-size: 0.8rem;
  color: #999;
}

.delete-button {
  padding: 0.35rem 0.75rem;
  border: 1px solid #dc3545;
  border-radius: 4px;
  background: #fff;
  color: #dc3545;
  font-size: 0.85rem;
  cursor: pointer;
}

.delete-button:hover:not(:disabled) {
  background: #dc3545;
  color: #fff;
}

.delete-button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.cards-table th {
  color: #34495e;
  font-weight: 600;
  background: #f8f9fa;
}

.message {
  margin-bottom: 1rem;
  padding: 0.75rem;
  border-radius: 6px;
  font-size: 0.9rem;
  text-align: center;
}

.message.error {
  background-color: #f8d7da;
  color: #721c24;
  border: 1px solid #f5c6cb;
}

.message.info {
  background-color: #d1ecf1;
  color: #0c5460;
  border: 1px solid #bee5eb;
}
</style>
